import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { api, type Me } from "../api";

/* The signed-in frame: side navigation on wide screens, a top bar with a
 * Menu button below 48rem.
 *
 * Hiding a link here is NOT gating. The route guards in App.tsx
 * (DomainsOnly, StaffOnly) are what stop a typed URL, and App.test.tsx
 * proves it. This only decides what is offered. */
export function AppShell({ me, children }: { me: Me; children: React.ReactNode }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const org = me.organizations.find((o) => o.id === me.active_org_id);
  const menuButton = useRef<HTMLButtonElement>(null);

  /* Closing the phone menu hides the link that was just used, so focus goes
     back to the Menu button rather than dropping to <body>. Only when the
     menu was open: on wide screens that button is not displayed. */
  const close = () => {
    if (!open) return;
    setOpen(false);
    menuButton.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      menuButton.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  async function signOut() {
    // Always land on /login, even if the logout POST fails or rejects --
    // a stuck "signed in" page with a dead session is worse than a client
    // that believes it signed out when the server-side session outlives it.
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* ignored: navigating to /login below is unconditional */
    }
    navigate("/login", { replace: true });
  }

  return (
    <div className="appshell" data-nav-open={open ? "true" : "false"}>
      <header className="topbar">
        <NavLink to="/services" className="brandmark" onClick={close}>
          <i className="brandmark__mark" aria-hidden="true" />
          IRONFORGE
        </NavLink>
        <button
          ref={menuButton}
          type="button"
          className="btn topbar__menu"
          aria-expanded={open}
          aria-controls="main-nav"
          onClick={() => setOpen((v) => !v)}
        >
          Menu
        </button>
      </header>

      <aside className="sidenav">
        <NavLink to="/services" className="brandmark sidenav__brand" onClick={close}>
          <i className="brandmark__mark" aria-hidden="true" />
          IRONFORGE
        </NavLink>
        {org && <div className="sidenav__org">{org.name}</div>}

        <nav id="main-nav" aria-label="Main" className="sidenav__links">
          <NavLink to="/services" end className="navlink" onClick={close}>
            Services
          </NavLink>
          <NavLink to="/services/new" className="navlink" onClick={close}>
            Order
          </NavLink>
          {me.features?.websites && (
            <NavLink to="/websites" className="navlink" onClick={close}>
              Websites
            </NavLink>
          )}
          {me.features?.domains && (
            <NavLink to="/domains" className="navlink" onClick={close}>
              Domains
            </NavLink>
          )}
          {me.features?.domains && (
            <NavLink to="/dns" className="navlink" onClick={close}>
              DNS
            </NavLink>
          )}
          {me.is_staff && (
            <>
              <div className="label sidenav__group">Staff</div>
              <NavLink to="/admin/clusters" className="navlink" onClick={close}>
                Clusters
              </NavLink>
              <NavLink to="/admin/discovery" className="navlink" onClick={close}>
                Discovery
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidenav__foot">
          <div className="sidenav__who mono">{me.email}</div>
          <button type="button" className="navlink" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="appshell__main">{children}</main>
    </div>
  );
}
